// src/chat/CallProvider.js
// Sesli arama durumu ve WebRTC bağlantısı. Panel kabuğunda bir kez kurulur;
// sohbet ekranı aramayı başlatır, CallOverlay her sayfada arama penceresini
// çizer. Sinyalleşme socket ack'leriyle gider, socket kopuksa REST'e düşer.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { chatApi, mapChatError } from "../api/chatApi";
import { CHAT_EVENTS, emitWithAck, onChatEvent, retainChatSocket, releaseChatSocket } from "./chatSocket";

const CallContext = createContext(null);

const IDLE = { status: "idle", call: null, error: "", muted: false, startedAt: null, remoteName: "", recording: false };
// Görüşmeler kayıt altına alınır (yöneticiler dinler). Tarayıcı MediaRecorder
// destekliyorsa yerel + uzak ses karıştırılıp görüşme sonunda yüklenir.
const RECORD_CALLS = process.env.REACT_APP_RECORD_CALLS !== "false";
const DEFAULT_ICE = [{ urls: ["stun:stun.l.google.com:19302"] }];
const RING_TIMEOUT_MS = 45000;

// Basit zil sesi: dış dosya gerektirmez, tarayıcı izin verdiği sürece çalar.
function makeRinger() {
  let ctx = null; let timer = null;
  const beep = () => {
    try {
      ctx ||= new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.type = "sine"; osc.frequency.value = 880; gain.gain.value = 0.08;
      osc.connect(gain).connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.35);
      const osc2 = ctx.createOscillator(); osc2.type = "sine"; osc2.frequency.value = 660; osc2.connect(gain);
      osc2.start(ctx.currentTime + 0.45); osc2.stop(ctx.currentTime + 0.8);
    } catch { /* ses izni yoksa sessiz */ }
  };
  return {
    start() { if (timer) return; beep(); timer = setInterval(beep, 2000); },
    stop() { if (timer) clearInterval(timer); timer = null; },
  };
}

export function CallProvider({ enabled = true, children }) {
  const [state, setState] = useState(IDLE);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const callRef = useRef(null);
  const pendingIceRef = useRef([]);       // arama kimliği belli olmadan üretilen yerel adaylar
  const remoteIceRef = useRef([]);        // pc kurulmadan gelen uzak adaylar
  const incomingSdpRef = useRef(null);
  const iceServersRef = useRef(DEFAULT_ICE);
  const ringTimerRef = useRef(null);
  const recorderRef = useRef(null);       // { recorder, chunks, ctx, startedAt, callId }
  const ringer = useMemo(makeRinger, []);

  const patch = useCallback((next) => setState((current) => ({ ...current, ...next })), []);

  /** Yerel ve uzak sesi tek akışta karıştırıp kaydeder; bitince yükler. */
  const startRecording = useCallback((remoteStream) => {
    // Yalnız yönetimle yapılan aramalar kaydedilir (sunucu: call.recordable).
    if (!RECORD_CALLS || !callRef.current?.recordable || recorderRef.current || typeof MediaRecorder === "undefined" || !streamRef.current) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const destination = ctx.createMediaStreamDestination();
      ctx.createMediaStreamSource(streamRef.current).connect(destination);
      if (remoteStream) ctx.createMediaStreamSource(remoteStream).connect(destination);
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"].find((type) => MediaRecorder.isTypeSupported(type)) || "";
      const recorder = new MediaRecorder(destination.stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      recorder.ondataavailable = (event) => { if (event.data && event.data.size) chunks.push(event.data); };
      recorder.start(1000);
      recorderRef.current = { recorder, chunks, ctx, startedAt: Date.now(), callId: callRef.current?.id, mime };
      patch({ recording: true });
    } catch { /* kayıt isteğe bağlı; görüşmeyi engellemez */ }
  }, [patch]);

  const stopRecordingAndUpload = useCallback(() => {
    const entry = recorderRef.current;
    recorderRef.current = null;
    if (!entry) return;
    const { recorder, chunks, ctx, startedAt, callId, mime } = entry;
    const finalize = () => {
      try { ctx.close(); } catch { /* kapalı */ }
      const durationMs = Date.now() - startedAt;
      if (!callId || !chunks.length || durationMs < 1500) return;
      const blob = new Blob(chunks, { type: (mime || "audio/webm").split(";")[0] });
      chatApi.uploadRecording(callId, blob, durationMs).catch(() => {});
    };
    try {
      if (recorder.state !== "inactive") { recorder.onstop = finalize; recorder.stop(); } else finalize();
    } catch { finalize(); }
  }, []);

  const cleanupMedia = useCallback(() => {
    ringer.stop();
    stopRecordingAndUpload();
    if (ringTimerRef.current) { clearTimeout(ringTimerRef.current); ringTimerRef.current = null; }
    try { pcRef.current?.close(); } catch { /* zaten kapalı */ }
    pcRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (audioRef.current) audioRef.current.srcObject = null;
    pendingIceRef.current = [];
    remoteIceRef.current = [];
    incomingSdpRef.current = null;
  }, [ringer, stopRecordingAndUpload]);

  const finish = useCallback((label) => {
    cleanupMedia();
    const call = callRef.current;
    callRef.current = null;
    setState({ ...IDLE, status: "ended", call, endedLabel: label || "" });
    setTimeout(() => setState((current) => (current.status === "ended" ? IDLE : current)), 2500);
  }, [cleanupMedia]);

  const signal = useCallback(async (event, payload, restFallback) => {
    try { return await emitWithAck(event, payload); }
    catch (error) {
      if (error?.message === "SOCKET_OFFLINE" || error?.message === "SOCKET_TIMEOUT") return restFallback();
      throw error;
    }
  }, []);

  const sendIce = useCallback((candidate) => {
    const call = callRef.current;
    if (!call?.id) { pendingIceRef.current.push(candidate); return; }
    signal("call:ice", { call_id: call.id, candidate }, () => chatApi.sendIce(call.id, candidate)).catch(() => {});
  }, [signal]);

  const flushPendingIce = useCallback(() => {
    const list = pendingIceRef.current; pendingIceRef.current = [];
    list.forEach(sendIce);
  }, [sendIce]);

  const createPeer = useCallback(async () => {
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    pcRef.current = pc;
    pc.onicecandidate = (event) => { if (event.candidate) sendIce(event.candidate.toJSON ? event.candidate.toJSON() : event.candidate); };
    pc.ontrack = (event) => {
      if (audioRef.current) {
        audioRef.current.srcObject = event.streams[0];
        audioRef.current.play().catch(() => {});
      }
      startRecording(event.streams[0]);
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") patch({ status: "active", startedAt: Date.now(), error: "" });
      if (["failed", "closed"].includes(pc.connectionState) && callRef.current) {
        const call = callRef.current;
        signal("call:end", { call_id: call.id, reason: "connection-failed" }, () => chatApi.endCall(call.id, { reason: "connection-failed" })).catch(() => {});
        finish("Bağlantı koptu");
      }
    };
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    streamRef.current = stream;
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));
    return pc;
  }, [finish, patch, sendIce, signal, startRecording]);

  const applyRemoteIce = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || !pc.remoteDescription) return;
    const list = remoteIceRef.current; remoteIceRef.current = [];
    for (const candidate of list) {
      try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch { /* geçersiz aday */ }
    }
  }, []);

  /* ---------- dışa açık eylemler ---------- */

  const startCall = useCallback(async (conversation) => {
    if (!conversation?.can_call) return;
    // Yönetim sohbetinde üye "ElitLig Yönetimi"ni arar; yönetici üyeyi arar.
    const remoteFallback = conversation.other_user || (conversation.is_management ? { name: "ElitLig Yönetimi", subtitle: "Yönetim" } : {});
    if (callRef.current) { patch({ error: "Zaten devam eden bir aramanız var." }); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === "undefined") {
      patch({ error: "Tarayıcınız sesli aramayı desteklemiyor." }); return;
    }
    const remote = remoteFallback;
    setState({ ...IDLE, status: "outgoing", remoteName: remote.name || conversation.title, call: { conversation_id: conversation.id, other_user: remote } });
    try {
      try { iceServersRef.current = (await chatApi.iceServers()).ice_servers || DEFAULT_ICE; } catch { iceServersRef.current = DEFAULT_ICE; }
      const pc = await createPeer();
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      const result = await signal("call:invite", { conversation_id: conversation.id, sdp: { type: offer.type, sdp: offer.sdp } }, () => chatApi.startCall({ conversation_id: conversation.id, sdp: { type: offer.type, sdp: offer.sdp } }));
      callRef.current = result.call;
      patch({ call: result.call, remoteName: result.call?.other_user?.name || remote.name || conversation.title });
      flushPendingIce();
      ringTimerRef.current = setTimeout(() => {
        const call = callRef.current;
        if (call && pcRef.current && !pcRef.current.remoteDescription) {
          signal("call:end", { call_id: call.id, reason: "timeout" }, () => chatApi.endCall(call.id, { reason: "timeout" })).catch(() => {});
          finish("Cevap yok");
        }
      }, RING_TIMEOUT_MS);
    } catch (error) {
      cleanupMedia();
      callRef.current = null;
      const message = error?.name === "NotAllowedError" ? "Mikrofon izni verilmedi." : mapChatError(error);
      setState({ ...IDLE, status: "ended", endedLabel: message, error: message });
      setTimeout(() => setState((current) => (current.status === "ended" ? IDLE : current)), 3500);
    }
  }, [cleanupMedia, createPeer, finish, flushPendingIce, patch, signal]);

  const acceptCall = useCallback(async () => {
    const call = callRef.current;
    const offer = incomingSdpRef.current;
    if (!call || state.status !== "incoming") return;
    ringer.stop();
    patch({ status: "connecting" });
    try {
      if (!offer) throw new Error("Arama teklifi alınamadı; karşı tarafın yeniden aramasını isteyin.");
      const pc = await createPeer();
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      await applyRemoteIce();
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await signal("call:accept", { call_id: call.id, sdp: { type: answer.type, sdp: answer.sdp } }, () => chatApi.acceptCall(call.id, { sdp: { type: answer.type, sdp: answer.sdp } }));
      flushPendingIce();
    } catch (error) {
      signal("call:end", { call_id: call.id, reason: "accept-failed" }, () => chatApi.endCall(call.id, { reason: "accept-failed" })).catch(() => {});
      const message = error?.name === "NotAllowedError" ? "Mikrofon izni verilmedi." : (error?.response ? mapChatError(error) : error.message);
      finish(message);
    }
  }, [applyRemoteIce, createPeer, finish, flushPendingIce, patch, ringer, signal, state.status]);

  const rejectCall = useCallback(async () => {
    const call = callRef.current;
    if (!call) return;
    signal("call:reject", { call_id: call.id }, () => chatApi.rejectCall(call.id)).catch(() => {});
    finish("Arama reddedildi");
  }, [finish, signal]);

  const endCall = useCallback(async () => {
    const call = callRef.current;
    if (!call) { cleanupMedia(); setState(IDLE); return; }
    signal("call:end", { call_id: call.id, reason: "hangup" }, () => chatApi.endCall(call.id, { reason: "hangup" })).catch(() => {});
    finish(state.status === "active" ? "Görüşme bitti" : "Arama iptal edildi");
  }, [cleanupMedia, finish, signal, state.status]);

  const toggleMute = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const next = !state.muted;
    stream.getAudioTracks().forEach((track) => { track.enabled = !next; });
    patch({ muted: next });
  }, [patch, state.muted]);

  /* ---------- socket olayları ---------- */

  useEffect(() => {
    if (!enabled) return undefined;
    retainChatSocket();
    const offs = [
      onChatEvent(CHAT_EVENTS.CALL_INCOMING, ({ call, sdp, ice_servers: ice }) => {
        if (callRef.current) return; // meşgul: sunucu zaten busy döner
        callRef.current = call;
        incomingSdpRef.current = sdp || null;
        if (Array.isArray(ice) && ice.length) iceServersRef.current = ice;
        setState({ ...IDLE, status: "incoming", call, remoteName: call?.other_user?.name || "Gelen arama" });
        ringer.start();
        ringTimerRef.current = setTimeout(() => { if (callRef.current?.id === call.id && !pcRef.current) finish("Cevapsız arama"); }, RING_TIMEOUT_MS);
      }),
      onChatEvent(CHAT_EVENTS.CALL_ACCEPTED, async ({ call, sdp }) => {
        if (!callRef.current || Number(callRef.current.id) !== Number(call?.id)) return;
        if (ringTimerRef.current) { clearTimeout(ringTimerRef.current); ringTimerRef.current = null; }
        patch({ status: "connecting", call });
        try {
          if (pcRef.current && sdp) { await pcRef.current.setRemoteDescription(new RTCSessionDescription(sdp)); await applyRemoteIce(); }
        } catch { finish("Bağlantı kurulamadı"); }
      }),
      onChatEvent(CHAT_EVENTS.CALL_ENDED, ({ call, reason }) => {
        if (!callRef.current || Number(callRef.current.id) !== Number(call?.id)) return;
        const labels = { rejected: "Arama reddedildi", timeout: "Cevapsız arama", cancelled: "Arama iptal edildi", missed: "Cevapsız arama", hangup: "Görüşme bitti", taken: "Aramayı başka bir yönetici yanıtladı" };
        finish(labels[reason] || labels[call?.status] || "Görüşme bitti");
      }),
      onChatEvent(CHAT_EVENTS.CALL_ICE, ({ call_id: callId, candidate }) => {
        if (!callRef.current || Number(callRef.current.id) !== Number(callId) || !candidate) return;
        remoteIceRef.current.push(candidate);
        applyRemoteIce();
      }),
      onChatEvent(CHAT_EVENTS.CALL_STATE, async ({ call_id: callId, sdp }) => {
        if (!callRef.current || Number(callRef.current.id) !== Number(callId) || !sdp || !pcRef.current) return;
        try { await pcRef.current.setRemoteDescription(new RTCSessionDescription(sdp)); await applyRemoteIce(); } catch { /* yeniden müzakere başarısız */ }
      }),
    ];
    return () => { offs.forEach((off) => off()); releaseChatSocket(); cleanupMedia(); };
  }, [applyRemoteIce, cleanupMedia, enabled, finish, patch, ringer]);

  const value = useMemo(() => ({ ...state, startCall, acceptCall, rejectCall, endCall, toggleMute, audioRef }), [state, startCall, acceptCall, rejectCall, endCall, toggleMute]);
  return <CallContext.Provider value={value}>{children}<audio ref={audioRef} autoPlay playsInline style={{ display: "none" }} /></CallContext.Provider>;
}

export const useCall = () => useContext(CallContext) || { ...IDLE, startCall: () => {}, acceptCall: () => {}, rejectCall: () => {}, endCall: () => {}, toggleMute: () => {} };
